import '@babel/polyfill';

import React, { FC } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { IntlProvider } from 'react-intl';
import { ModalProvider } from '../../components/modal/ModalProvider';
import { defaultCurrentUser, UserProvider } from '../../user/UserContext';
import { getLocalMessages } from '../../translateUtils';
import { isClientSide } from '../../utils/ssrUtils';
import { UserCustomWindow, UserContextType } from '../../user/user.types';
import { snakeToCamel } from '../../caseUtils';
import { USER_CURRENT_STATUS } from '../../../../shared/constants/userStatusConstants';
import { USER_ONBOARDING } from '../../../../shared/constants/userOnboardingConstants';

declare let window: UserCustomWindow;

/** Old accounts haven't firstName + lastName so we fallback on userName */
const getUserName = (firstName: string | null, userName: string) => {
  if (firstName) {
    return firstName;
  }
  if (userName) {
    return userName;
  }
  return '';
};

const getUserFromWindow = (): UserContextType['currentUser'] => {
  const currentUser: UserContextType['currentUser'] = window.current_user
    ? snakeToCamel(window.current_user)
    : {};

  return {
    ...cloneDeep(defaultCurrentUser),
    ...currentUser,
    name: getUserName(currentUser.firstName, currentUser.userName),
    isExpired: currentUser.status === USER_CURRENT_STATUS.EXPIRED_FREE_TRIAL,
    isOnboardedOnWatchers:
      currentUser.wasOnboarded &&
      currentUser.wasOnboarded.includes(USER_ONBOARDING.SEE_WATCHERS_INBOX),
    isOnboardedOnHistory:
      currentUser.wasOnboarded &&
      currentUser.wasOnboarded.includes(USER_ONBOARDING.SEE_LAST_HISTORY_ITEM),
    isIcp:
      isClientSide &&
      window.METIERS_IS_ICP &&
      !!currentUser.metier &&
      window.METIERS_IS_ICP.includes(currentUser.metier),
  };
};

export const CommonAppProviders: FC<{ isLoggedIn: boolean }> = ({
  children,
  isLoggedIn,
}) => {
  let user;
  let countryCode;
  let accessPremium;

  if (isClientSide) {
    user = getUserFromWindow();
    countryCode = window.COUNTRY_CODE || 'fr';
    accessPremium = window.access_premium;
  } else {
    countryCode = 'fr';
  }

  return (
    <IntlProvider locale={countryCode} messages={getLocalMessages()}>
      <UserProvider
        user={user}
        accessPremium={accessPremium}
        loggedIn={isLoggedIn}
      >
        <ModalProvider>{children}</ModalProvider>
      </UserProvider>
    </IntlProvider>
  );
};
